/*
 * UBQ-5897: only the zone NAMES are used here (offsets come from Intl), and every moment-timezone
 * build lists the same 597 names. The default entry bundles ~700 kB of historical transition data
 * into the main bundle; this build carries a 10-year slice (~40 kB). Do not switch to
 * `moment-timezone` or `-with-data` without checking the main bundle size.
 */
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range'

/**
 * List of available IANA time zones.
 *
 * We source the list from moment-timezone's bundled tz database rather than
 * `Intl.supportedValuesOf('timeZone')` because the native API is not consistent
 * across browsers: Firefox (SpiderMonkey) omits non-canonical tz-database
 * link/alias zones (e.g. "Asia/Calcutta", "America/Buenos_Aires") that
 * Chromium (V8) reports, so those zones went missing from the dropdown in
 * Firefox only (UBQ-5472). moment-timezone ships a fixed dataset that is
 * identical in every browser, and we union it with whatever `supportedValuesOf`
 * reports so a browser that ever exposes extra zones still contributes them.
 */
export const getAllTimezones = (): string[] => {
    const zoneSet = new Set<string>(moment.tz.names())

    if (
        typeof Intl !== 'undefined' &&
        typeof (Intl as any).supportedValuesOf === 'function'
    ) {
        for (const tz of (Intl as any).supportedValuesOf(
            'timeZone',
        ) as string[]) {
            zoneSet.add(tz)
        }
    }

    // moment.tz.names() already includes "UTC", but guard anyway so public-API
    // created shipments (which always store timezone as "UTC") can be correctly
    // displayed and selected in the timezone dropdown.
    zoneSet.add('UTC')

    return Array.from(zoneSet)
}

// gmt offset string format
export function getGmtOffsetString(timeZone: string): {
    label: string
    offsetMinutes: number
} {
    const now = new Date()
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    })
    const parts = dtf.formatToParts(now)
    const get = (type: string) =>
        Number(parts.find((p) => p.type === type)?.value)

    const asUTC = Date.UTC(
        get('year'),
        get('month') - 1,
        get('day'),
        get('hour'),
        get('minute'),
        get('second'),
    )
    const offsetMinutes = (asUTC - now.getTime()) / 60000
    const sign = offsetMinutes <= -1 ? '-' : '+'
    const absMinutes = Math.abs(offsetMinutes)
    const totalMinutes = Math.round(absMinutes)
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, '0')
    const minutes = String(totalMinutes % 60).padStart(2, '0')

    return {
        label: `GMT${sign}${hours}:${minutes}`,
        offsetMinutes,
    }
}

// Module-level cache — timezone offsets are stable for the lifetime of the session.
let _allTimezoneOptionsCache:
    | {
          label: string
          value: string
          offsetMinutes: number
      }[]
    | null = null

export function getAllTimezoneOptions(): {
    label: string
    value: string
    offsetMinutes: number
}[] {
    if (_allTimezoneOptionsCache) return _allTimezoneOptionsCache
    /* moment-timezone's bundled tz DB can list zones newer than the browser's
       ICU data (e.g. America/Coyhaique), for which Intl.DateTimeFormat throws a
       RangeError. Skip those zones so one of them can't take down the page
       (UBQ-5756). */
    _allTimezoneOptionsCache = getAllTimezones().flatMap((tz) => {
        try {
            const { label, offsetMinutes } = getGmtOffsetString(tz)
            return [{ label: `(${label}) ${tz}`, value: tz, offsetMinutes }]
        } catch {
            return []
        }
    })
    return _allTimezoneOptionsCache
}

// cache for timezone labels to avoid repeated lookups
const timezoneLabelCache: Record<string, string> = {}

export function getSelectedTimezoneLabel(preferredTimeZone: string): string {
    if (timezoneLabelCache[preferredTimeZone]) {
        return timezoneLabelCache[preferredTimeZone]
    }
    const label =
        getAllTimezoneOptions().find((opt) => opt.value === preferredTimeZone)
            ?.label || preferredTimeZone
    timezoneLabelCache[preferredTimeZone] = label
    return label
}
